import React, { useEffect, useMemo, useState } from "react";

import { getUserPhotoHeroSourceGroups } from "./userPhotoHero";

const DEFAULT_TEASERS = [
  { label: "Best cut", score: "97%" },
  { label: "Best length", score: "96%" },
  { label: "Best styling", score: "94%" },
];

const PALETTE_SWATCH_SETS = [
  [
    "#D7B89C",
    "#C98E72",
    "#A45E4D",
    "#6F483B",
    "#E8D6B7",
    "#DAB56D",
    "#9F7A4D",
    "#5A4634",
    "#C4CFA1",
    "#8FA676",
    "#657B5B",
    "#EDE3D0",
  ],
  [
    "#D8DCE4",
    "#AEB8C8",
    "#7D8CA5",
    "#4B5870",
    "#E7DDE8",
    "#C8B2CF",
    "#9E7EAA",
    "#6F567B",
    "#C6D9DC",
    "#91B8C0",
    "#5F8994",
    "#F0EEF2",
  ],
  [
    "#F2C7B8",
    "#E69B8E",
    "#C86C75",
    "#8D4E66",
    "#F3E2A8",
    "#D5C96D",
    "#A0A96A",
    "#5F7E66",
    "#BBD7D5",
    "#82B4B7",
    "#5F7E9C",
    "#EFE8D9",
  ],
];

const RECOMMENDATION_PREVIEW_TYPES = ["glasses", "hairstyle", "contouring"];
const OVERLAY_RETRY_DELAYS_MS = [1500, 3000];

const UserPhotoHeroShowcase = ({
  badgeText = "Face shape detected",
  fallbackClassName = "max-w-full w-full rounded-lg",
  fallbackSrc,
  teaserVariant = "photo",
  teasers = DEFAULT_TEASERS,
}) => {
  const search = window.location.search;
  const { overlaySources, originalPhotoSource, orderedSources: userPhotoSources } =
    useMemo(() => getUserPhotoHeroSourceGroups(search), [search]);
  const overlaySource = overlaySources[0];
  const sourceKey = JSON.stringify([overlaySource, originalPhotoSource]);
  const [loadedSource, setLoadedSource] = useState(null);
  const currentSrc = loadedSource?.key === sourceKey
    ? loadedSource.src
    : originalPhotoSource || fallbackSrc;
  const hasUserPhoto = userPhotoSources.length > 0;

  useEffect(() => {
    if (!overlaySource) return undefined;

    let attempts = 0;
    let stopped = true;
    let loaded = false;
    let activeImage = null;
    let timer;

    const clearImage = () => {
      if (!activeImage) return;
      activeImage.onload = null;
      activeImage.onerror = null;
      activeImage.removeAttribute("src");
      activeImage = null;
    };
    const stop = () => {
      stopped = true;
      window.clearTimeout(timer);
      clearImage();
    };
    const attempt = () => {
      if (stopped || loaded || attempts >= 3) return;
      attempts += 1;
      const image = new Image();
      activeImage = image;
      image.fetchPriority = "high";
      image.onload = () => {
        if (stopped || activeImage !== image) return;
        loaded = true;
        setLoadedSource({ key: sourceKey, src: overlaySource });
        clearImage();
      };
      image.onerror = () => {
        if (stopped || activeImage !== image) return;
        clearImage();
        if (attempts < 3) {
          timer = window.setTimeout(attempt, OVERLAY_RETRY_DELAYS_MS[attempts - 1]);
        }
      };
      image.src = overlaySource;
    };
    const start = () => {
      if (!stopped) return;
      stopped = false;
      window.clearTimeout(timer);
      // Defer one task so StrictMode's setup/cleanup does not duplicate requests.
      if (!activeImage && !loaded && attempts < 3) timer = window.setTimeout(attempt, 0);
    };

    start();
    window.addEventListener("pagehide", stop);
    window.addEventListener("pageshow", start);
    return () => {
      stop();
      window.removeEventListener("pagehide", stop);
      window.removeEventListener("pageshow", start);
    };
  }, [overlaySource, sourceKey]);

  const handleHeroError = () => {
    const nextSource = currentSrc === overlaySource && originalPhotoSource
      ? originalPhotoSource
      : fallbackSrc;
    setLoadedSource({ key: sourceKey, src: nextSource });
  };

  if (!hasUserPhoto) {
    return (
      <img
        src={fallbackSrc}
        alt="welcome"
        fetchPriority="high"
        className={fallbackClassName}
      />
    );
  }

  const renderTeaserPreview = (item, index) => {
    if (teaserVariant === "recommendation") {
      const type = item.previewType || RECOMMENDATION_PREVIEW_TYPES[index % RECOMMENDATION_PREVIEW_TYPES.length];

      return (
        <div
          aria-hidden="true"
          className="absolute inset-0 overflow-hidden blur-[5px] brightness-105 saturate-90"
          data-testid="recommendation-teaser-preview"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#F7E9EF] via-[#F8F6F0] to-[#E7DDE8]" />

          {type === "glasses" && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative h-14 w-24 sm:h-16 sm:w-28">
                <div className="absolute left-0 top-2 h-11 w-11 rounded-full border-[5px] border-[#2F2933]/70 bg-white/35 shadow-sm sm:h-12 sm:w-12" />
                <div className="absolute right-0 top-2 h-11 w-11 rounded-full border-[5px] border-[#2F2933]/70 bg-white/35 shadow-sm sm:h-12 sm:w-12" />
                <div className="absolute left-[42%] top-8 h-1.5 w-5 rounded-full bg-[#2F2933]/75 sm:top-9" />
                <div className="absolute -left-3 top-6 h-2 w-8 -rotate-12 rounded-full bg-[#2F2933]/45" />
                <div className="absolute -right-3 top-6 h-2 w-8 rotate-12 rounded-full bg-[#2F2933]/45" />
              </div>
            </div>
          )}

          {type === "hairstyle" && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative h-24 w-20 sm:h-28 sm:w-24">
                <div className="absolute inset-x-1 top-0 h-20 rounded-t-full bg-[#5F463E]/75" />
                <div className="absolute left-0 top-8 h-16 w-9 rounded-full bg-[#6E5248]/65" />
                <div className="absolute right-0 top-8 h-16 w-9 rounded-full bg-[#6E5248]/65" />
                <div className="absolute left-1/2 top-9 h-16 w-14 -translate-x-1/2 rounded-full bg-[#F0C9B8]/60" />
                <div className="absolute left-5 top-5 h-8 w-9 rounded-full bg-[#B58A72]/70" />
              </div>
            </div>
          )}

          {type === "contouring" && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative h-24 w-20 sm:h-28 sm:w-24">
                <div className="absolute inset-x-3 top-1 h-24 rounded-full border-[4px] border-[#7C3A8D]/35 bg-[#F0C9B8]/45 sm:h-28" />
                <div className="absolute left-3 top-10 h-2 w-10 -rotate-[35deg] rounded-full bg-[#A45E4D]/55" />
                <div className="absolute right-3 top-10 h-2 w-10 rotate-[35deg] rounded-full bg-[#A45E4D]/55" />
                <div className="absolute left-1/2 top-8 h-16 w-2 -translate-x-1/2 rounded-full bg-white/55" />
                <div className="absolute left-1/2 bottom-5 h-2 w-12 -translate-x-1/2 rounded-full bg-[#7C3A8D]/35" />
              </div>
            </div>
          )}
        </div>
      );
    }

    if (teaserVariant === "palette") {
      const swatches = item.swatches || PALETTE_SWATCH_SETS[index % PALETTE_SWATCH_SETS.length];

      return (
        <div
          aria-hidden="true"
          className="absolute inset-0 grid grid-cols-4 gap-1 p-2 blur-[5px] brightness-105 saturate-90"
          data-testid="palette-teaser-preview"
        >
          {swatches.map((color, swatchIndex) => (
            <div
              key={`${item.label}-${color}-${swatchIndex}`}
              className="rounded-[4px]"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      );
    }

    return (
      <img
        src={currentSrc}
        alt=""
        aria-hidden="true"
        className="h-full w-full scale-110 object-cover blur-[8px] brightness-90 saturate-75"
        data-testid="photo-teaser-preview"
      />
    );
  };

  return (
    <div className="grid w-full grid-cols-[minmax(0,1fr)_minmax(92px,0.42fr)] items-stretch gap-2 sm:gap-3">
      <div className="relative overflow-hidden rounded-lg bg-[#F8F6F0]">
        <img
          src={currentSrc}
          alt="welcome"
          fetchPriority="high"
          className="block h-full min-h-[360px] w-full object-cover sm:min-h-[440px]"
          onError={handleHeroError}
        />
        <div className="absolute inset-x-3 bottom-3 rounded-full bg-black/70 px-3 py-2 text-center text-[11px] font-bold uppercase tracking-normal text-white shadow-sm sm:inset-x-4 sm:bottom-4 sm:text-xs">
          {badgeText}
        </div>
      </div>

      <div className="grid min-h-[360px] grid-rows-3 gap-2 sm:min-h-[440px] sm:gap-3">
        {teasers.map((item, index) => (
          <div key={item.label} className="relative overflow-hidden rounded-lg bg-[#F8F6F0]">
            {renderTeaserPreview(item, index)}
            <div className="absolute inset-0 bg-white/45" />
            <div className="absolute inset-x-2 top-2 rounded-full bg-black/70 px-2 py-1 text-center text-[10px] font-bold uppercase tracking-normal text-white sm:text-xs">
              {item.score} match
            </div>
            <div className="absolute inset-x-2 bottom-2 rounded-md bg-white/85 px-2 py-1 text-center text-[10px] font-extrabold leading-tight text-[#7C3A8D] shadow-sm sm:text-xs">
              {item.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserPhotoHeroShowcase;
